/**
 * CARLOS OS — Vanilla JS app.
 * Sin Alpine, sin dependencias frágiles. Funciona en cualquier navegador moderno.
 *
 * Flujo:
 * 1. Ocultar splash inmediatamente al cargar el módulo
 * 2. Pintar pantalla de login estática
 * 3. Al hacer login/signup → bootstrap y mostrar dashboard
 */

// ============================================================
// ESTADO GLOBAL
// ============================================================
const state = {
  user: null,
  needsSignup: false,
  needsOnboarding: false,
  active: 'inicio',
  apiStatus: 'checking',
};

const API_BASE = '';  // mismo origen via Caddy
let CUSTOM_CFG = { version: 1, customSections: [], navOverrides: {} };

// ============================================================
// HELPERS DOM
// ============================================================
function h(tag, attrs = {}, children = []) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = v;
    else if (k === 'style') el.style.cssText = v;
    else if (k === 'html') el.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null || c === false) continue;
    el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
  return el;
}

function $(sel) { return document.querySelector(sel); }

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'light') {
    root.classList.add('light-theme');
    document.body?.style.setProperty('background', '#FBF8F2');
    document.body?.style.setProperty('color', '#1a1814');
  } else {
    root.classList.remove('light-theme');
    document.body?.style.removeProperty('background');
    document.body?.style.removeProperty('color');
  }
  const btn = document.getElementById('theme-toggle');
  if (btn) btn.textContent = theme === 'light' ? '🌙' : '☀️';
}

function toggleTheme() {
  const current = localStorage.getItem('theme') || 'dark';
  const next = current === 'light' ? 'dark' : 'light';
  localStorage.setItem('theme', next);
  applyTheme(next);
}

// Apply on load
if (typeof window !== 'undefined') {
  const saved = localStorage.getItem('theme');
  if (saved) applyTheme(saved);
}
function mount(parent, content) {
  parent.innerHTML = '';
  parent.appendChild(content);
}

// ============================================================
// API CLIENT
// ============================================================
async function api(method, path, body) {
  const res = await fetch(API_BASE + path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(15_000),  // timeout 15s para que nunca cuelgue UI
  });
  const ct = res.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const err = new Error((data && data.message) || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

// ============================================================
// PANTALLAS
// ============================================================

function renderLogin() {
  const root = $('#app');
  const form = h('form', {
    class: 'bg-base-800/70 backdrop-blur border border-base-700 rounded-2xl p-6 space-y-4 shadow-soft',
    onsubmit: async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type=submit]');
      btn.disabled = true;
      btn.textContent = 'Procesando…';
      const errBox = form.querySelector('.err');
      errBox.classList.add('hidden');
      try {
        const data = state.needsSignup
          ? { email: form.email.value, password: form.password.value, fullName: form.fullName?.value || undefined }
          : { email: form.email.value, password: form.password.value };
        await api('POST', state.needsSignup ? '/api/v1/auth/signup' : '/api/v1/auth/login', data);
        const me = await api('GET', '/api/v1/auth/me');
        state.user = me;
        state.needsOnboarding = !me.onboardingCompletedAt;
        state.needsSignup = false;
        await renderApp();
      } catch (e) {
        errBox.textContent = e.message || 'Error de autenticación';
        errBox.classList.remove('hidden');
        btn.disabled = false;
        btn.textContent = state.needsSignup ? 'Crear cuenta' : 'Entrar';
      }
    },
  }, [
    h('div', {}, [
      h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Email'),
      h('input', { type: 'email', name: 'email', required: true, autocomplete: 'email', value: 'garciagrouphold@gmail.com',
        class: 'w-full bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-ink-100 focus:border-gold-400 focus:outline-none transition-colors' }),
    ]),
    h('div', {}, [
      h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Contraseña'),
      h('input', { type: 'password', name: 'password', required: true, autocomplete: state.needsSignup ? 'new-password' : 'current-password',
        placeholder: state.needsSignup ? 'Mín 8 caracteres' : '••••••••',
        class: 'w-full bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-ink-100 focus:border-gold-400 focus:outline-none transition-colors' }),
    ]),
    state.needsSignup ? h('div', {}, [
      h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Nombre completo (opcional)'),
      h('input', { type: 'text', name: 'fullName', autocomplete: 'name', placeholder: 'Carlos García',
        class: 'w-full bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-ink-100 focus:border-gold-400 focus:outline-none transition-colors' }),
    ]) : null,
    h('button', { type: 'submit',
      class: 'w-full bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-300 hover:to-gold-400 text-base-900 font-semibold py-3 rounded-lg transition-all shadow-gold disabled:opacity-50',
    }, state.needsSignup ? 'Crear cuenta' : 'Entrar'),
    h('div', { class: 'err text-red-300 text-sm text-center hidden' }),
  ]);

  const screen = h('div', { class: 'flex-1 flex items-center justify-center px-6 py-12' }, [
    h('div', { class: 'w-full max-w-md' }, [
      h('div', { class: 'text-center mb-8' }, [
        h('div', { class: 'w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-300 to-gold-500 mx-auto mb-4 flex items-center justify-center font-display font-bold text-3xl text-base-900 shadow-gold' }, 'C'),
        h('h1', { class: 'font-display text-3xl font-bold' }, [
          'CARLOS',
          h('span', { class: 'text-gold-400' }, ' OS'),
        ]),
        h('p', { class: 'text-ink-300 text-sm mt-1' }, state.needsSignup ? 'Configura tu acceso para empezar' : 'Tu sistema personal'),
      ]),
      form,
      h('p', { class: 'text-center text-xs text-ink-400 mt-6' }, 'CARLOS OS — privado · v0.4.0'),
    ]),
  ]);
  mount(root, screen);
}

// ============================================================
// DASHBOARD (después del login)
// ============================================================
// NAV reestructurado en GRUPOS desplegables — Tareas como item directo (acceso rápido).
//   🏠 Inicio · ✅ Tareas · 💰 Patrimonio
//   🏢 Empresas ▾ (Autosell, Inmuebles, Marketplace)
//   🌟 Personal ▾ (Mañana, Salud, Proyectos, Notas)
//   🤖 IA ▾ (Chat IA, Descubrir, Inteligencia)
//   📁 Archivos · ⚙️ Sistema ▾ (Monitor, Ajustes)
const NAV = [
  { id: 'inicio',     label: 'Inicio',     emoji: '🏠' },
  { id: 'tareas',     label: 'Tareas',     emoji: '✅' },
  { id: 'patrimonio', label: 'Patrimonio', emoji: '💰' },
  { id: 'empresas',   label: 'Empresas',   emoji: '🏢', group: true, children: [
    { id: 'autosell',    label: 'Autosell',    emoji: '🚗' },
    { id: 'inmuebles',   label: 'Inmuebles',   emoji: '🏘️' },
    { id: 'renovatech',  label: 'Renovatech',  emoji: '🔧' },
    { id: 'coches-suiza', label: 'Coches Suiza', emoji: '🚗🇨🇭' },
    { id: 'meta-ads',    label: 'Meta Ads',    emoji: '📣' },
    { id: 'bodega',      label: 'Bodega',      emoji: '🍷' },
  ]},
  { id: 'personal',   label: 'Personal',   emoji: '🌟', group: true, children: [
    { id: 'gastos',    label: 'Gastos',    emoji: '💸' },
    { id: 'salud',     label: 'Salud',     emoji: '💪' },
    { id: 'proyectos', label: 'Proyectos', emoji: '📋' },
    { id: 'notas',     label: 'Notas',     emoji: '📝' },
    { id: 'archivos',  label: 'Archivos',  emoji: '📁' },
  ]},
  { id: 'ia',         label: 'IA',         emoji: '🤖', group: true, children: [
    { id: 'chat',         label: 'Chat IA',      emoji: '💬' },
    { id: 'descubrir',    label: 'Descubrir',    emoji: '🔍' },
    { id: 'inteligencia', label: 'Inteligencia', emoji: '🛰️' },
    { id: 'youtube',      label: 'YouTube',      emoji: '🎬' },
    { id: 'prompts',      label: 'Prompts',      emoji: '📚' },
    { id: 'agency',       label: 'AI Agency',    emoji: '💼' },
    { id: 'trading',      label: 'Trading Bot',  emoji: '🤖' },
  ]},
  { id: 'sistema-group', label: 'Sistema', emoji: '⚙️', group: true, children: [
    { id: 'sistema', label: 'Monitor', emoji: '🖥️' },
    { id: 'equipo',  label: 'Equipo',  emoji: '👥' },
    { id: 'ajustes', label: 'Ajustes', emoji: '⚙️' },
  ]},
];

function effectiveNav() {
  const ov = (CUSTOM_CFG && CUSTOM_CFG.navOverrides) || {};
  const items = JSON.parse(JSON.stringify(NAV));
  const out = [];
  items.forEach((it, idx) => {
    const o = ov[it.id] || {};
    if (o.hidden) return;
    if (o.label) it.label = o.label;
    it._ord = (o.order != null ? o.order : idx);
    if (Array.isArray(it.children)) {
      it.children = it.children
        .filter((c) => !(ov[c.id] && ov[c.id].hidden))
        .map((c, ci) => { const co = ov[c.id] || {}; if (co.label) c.label = co.label; c._ord = (co.order != null ? co.order : ci); return c; })
        .sort((a, b) => a._ord - b._ord);
    }
    out.push(it);
  });
  const cs = ((CUSTOM_CFG && CUSTOM_CFG.customSections) || [])
    .filter((s) => !s.hidden)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((s) => ({ id: s.id, label: s.label, emoji: s.emoji || '\u{1F9E9}' }));
  const misChildren = cs.concat([{ id: 'constructor', label: 'Constructor', emoji: '\u{1F6E0}\uFE0F' }]);
  out.push({ id: 'mis', label: 'Mis secciones', emoji: '\u{1F9E9}', group: true, children: misChildren });
  out.sort((a, b) => ((a._ord != null ? a._ord : 99) - (b._ord != null ? b._ord : 99)));
  return out;
}

async function renderApp() {
  const _bodegaOnly = ['padre@bodega7hermanos.es'].includes(((state.user && state.user.email) || '').toLowerCase());
  try { const r = await api('GET', '/api/v1/ui-config'); CUSTOM_CFG = (r && r.config) ? r.config : CUSTOM_CFG; } catch (e) {}
  if (!CUSTOM_CFG.customSections) CUSTOM_CFG.customSections = [];
  if (!CUSTOM_CFG.navOverrides) CUSTOM_CFG.navOverrides = {};
  window.__BODEGA_READONLY__ = false; // el padre tambien puede editar (solo ve Bodega igualmente)
  if (_bodegaOnly) { state.needsOnboarding = false; if (state.active !== 'bodega') state.active = 'bodega'; }
  // Si necesita onboarding, mostrarlo
  if (state.needsOnboarding) {
    const { renderOnboarding } = await import('./onboarding/index.js');
    const root = $('#app');
    const container = h('div', { id: 'onboarding-mount', class: 'flex-1' });
    mount(root, container);
    renderOnboarding(container, {
      user: state.user,
      onComplete: () => {
        state.needsOnboarding = false;
        renderApp();
      },
    });
    return;
  }

  const root = $('#app');
  const header = h('header', { class: 'border-b border-base-700 bg-base-800/60 backdrop-blur-md sticky top-0 z-40' }, [
    h('div', { class: 'max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-3' }, [
      h('div', { class: 'flex items-center gap-3' }, [
        h('div', { class: 'w-9 h-9 rounded-lg bg-gradient-to-br from-gold-300 to-gold-500 shadow-gold flex items-center justify-center font-display font-bold text-base-900' }, 'C'),
        h('h1', { class: 'font-display font-bold text-xl' }, [
          'CARLOS',
          h('span', { class: 'text-gold-400' }, ' OS'),
        ]),
      ]),
      h('div', { class: 'flex items-center gap-3' }, [
        // Live sync indicator (con tooltip al hover)
        h('div', {
          id: 'sync-pill',
          class: 'hidden md:flex items-center gap-1.5 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-2 py-1 cursor-pointer hover:bg-emerald-500/20 transition',
          title: 'Sincronizando con plataformas en directo',
          onclick: () => navigate('sistema'),
        }, [
          h('span', { class: 'w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse' }),
          h('span', { id: 'sync-pill-text' }, 'En vivo'),
        ]),
        h('span', { id: 'api-dot', class: 'w-2 h-2 rounded-full bg-emerald-400 animate-pulse' }),
        h('button', {
          id: 'theme-toggle',
          class: 'text-ink-300 hover:text-ink-100 text-lg',
          title: 'Cambiar tema (claro/oscuro)',
          onclick: () => toggleTheme(),
        }, localStorage.getItem('theme') === 'light' ? '🌙' : '☀️'),
        h('button', {
          class: 'text-ink-300 hover:text-ink-100 text-xs',
          onclick: async () => {
            try { await api('POST', '/api/v1/auth/logout'); } catch {}
            state.user = null;
            state.needsOnboarding = false;
            renderLogin();
          },
        }, 'salir'),
      ]),
    ]),
    // Nav reestructurado con dropdowns para grupos
    h('div', { class: 'hidden md:block border-t border-base-700 overflow-x-auto relative' }, [
      h('div', { class: 'max-w-7xl mx-auto flex gap-1 px-3 py-2 min-w-max' },
        (_bodegaOnly ? [{ id: 'bodega', label: 'Bodega', emoji: '🍷' }] : effectiveNav()).map((item) => {
          if (item.group) return renderNavGroup(item);
          const isActive = state.active === item.id;
          return h('button', {
            'data-section': item.id,
            class: (isActive
              ? 'bg-gold-400/10 text-gold-300 border border-gold-400/30'
              : 'text-ink-200 hover:text-ink-100 border border-transparent') + ' px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
            onclick: () => navigate(item.id),
          }, [
            h('span', {}, item.emoji),
            h('span', { class: 'ml-1' }, item.label),
          ]);
        }),
      ),
    ]),
  ]);

  const main = h('main', { id: 'section-mount', class: 'flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 py-5 md:py-8 pb-28 md:pb-8' });

  const footer = h('footer', { class: 'border-t border-base-700 mt-12' }, [
    h('div', { class: 'max-w-7xl mx-auto px-6 py-4 text-xs text-ink-400 flex items-center justify-between' }, [
      h('span', {}, 'CARLOS OS — privado · Alicante'),
      h('span', {}, '© 2026 · garciagrouphold'),
    ]),
  ]);

  const layout = h('div', { class: 'min-h-screen flex flex-col' }, [header, main, footer, renderBottomNav()]);
  mount(root, layout);

  await renderSection(state.active);

  // Health poll
  pollHealth();

  // Smart Questions widget
  try {
    const { mountSmartQuestionsWidget } = await import('./components/smart-question.js');
    mountSmartQuestionsWidget();
  } catch (e) { console.warn('Smart Questions widget no cargó', e); }

  // Cmd+K búsqueda global
  try {
    const { setupCmdK } = await import('./components/cmdk.js');
    setupCmdK((section) => navigate(section));
  } catch (e) { console.warn('Cmd+K no cargó', e); }
}

// Render de grupo desplegable (Empresas, Personal, IA, Sistema)
// El menú se monta en document.body como PORTAL con position:fixed para escapar
// del overflow-x-auto del nav que lo recortaría.
function renderNavGroup(group) {
  const isAnyChildActive = group.children.some((c) => c.id === state.active);
  const button = h('button', {
    'data-group': group.id,
    'aria-haspopup': 'true',
    'aria-expanded': 'false',
    class: (isAnyChildActive
      ? 'bg-gold-400/10 text-gold-300 border border-gold-400/30'
      : 'text-ink-200 hover:text-ink-100 border border-transparent') + ' px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1',
  }, [
    h('span', {}, group.emoji),
    h('span', { class: 'ml-1' }, group.label),
    h('span', { class: 'text-xs ml-0.5 opacity-70' }, '▾'),
  ]);

  // Helper para crear y mostrar el dropdown en el body
  function showMenu() {
    // Cerrar cualquier otro abierto
    closeAllNavMenus();
    const rect = button.getBoundingClientRect();
    const menu = h('div', {
      'data-group-menu-portal': group.id,
      class: 'fixed bg-base-900 border border-base-700 rounded-xl shadow-2xl py-2 min-w-[200px] animate-fade-in',
      style: `top: ${rect.bottom + 4}px; left: ${rect.left}px; z-index: 9999;`,
    }, group.children.map((c) => {
      const active = state.active === c.id;
      return h('button', {
        'data-section': c.id,
        class: `block w-full text-left px-4 py-2.5 text-sm transition hover:bg-base-800 ${active ? 'bg-gold-400/10 text-gold-300' : 'text-ink-200'}`,
        onclick: (e) => {
          e.stopPropagation();
          navigate(c.id);
          closeAllNavMenus();
        },
      }, [
        h('span', { class: 'mr-2' }, c.emoji),
        h('span', {}, c.label),
      ]);
    }));
    document.body.appendChild(menu);
    button.setAttribute('aria-expanded', 'true');
  }

  button.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = button.getAttribute('aria-expanded') === 'true';
    if (open) closeAllNavMenus();
    else showMenu();
  });
  return button;
}

function closeAllNavMenus() {
  document.querySelectorAll('[data-group-menu-portal]').forEach((el) => el.remove());
  document.querySelectorAll('[data-group]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
}

// Listener global: click fuera o scroll/resize cierra
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-group]') && !e.target.closest('[data-group-menu-portal]')) {
      closeAllNavMenus();
    }
  });
  window.addEventListener('resize', closeAllNavMenus);
  window.addEventListener('scroll', closeAllNavMenus, true);
}


// ===== Barra inferior tipo app (solo movil) =====
const BOTTOM_TABS = [
  { id: 'inicio', label: 'Inicio', emoji: '🏠' },
  { id: 'tareas', label: 'Tareas', emoji: '✅' },
  { id: 'gastos', label: 'Gastos', emoji: '💸' },
  { id: 'salud',  label: 'Salud',  emoji: '💪' },
  { id: 'notas',  label: 'Notas',  emoji: '📝' },
];
function renderBottomNav() {
  if (window.__BODEGA_READONLY__) return h('span');
  const btn = (t) => h('button', {
    'data-bottom-tab': t.id,
    class: 'flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 rounded-xl transition-colors ' + (state.active === t.id ? 'text-gold-300' : 'text-ink-300'),
    onclick: () => { closeMoreSheet(); navigate(t.id); },
  }, [
    h('span', { class: 'text-xl leading-none' }, t.emoji),
    h('span', { class: 'text-[10px] font-semibold' }, t.label),
  ]);
  return h('nav', {
    id: 'bottom-nav',
    class: 'md:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-base-700 bg-base-800/95 backdrop-blur-md',
    style: 'padding-bottom: env(safe-area-inset-bottom, 0px);',
  }, [
    h('div', { class: 'flex items-stretch px-2 pt-1 pb-1' }, [
      ...BOTTOM_TABS.map(btn),
      h('button', {
        id: 'more-tab',
        class: 'flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 rounded-xl transition-colors text-ink-300',
        onclick: toggleMoreSheet,
      }, [
        h('span', { class: 'text-xl leading-none' }, '☰'),
        h('span', { class: 'text-[10px] font-semibold' }, 'Más'),
      ]),
    ]),
  ]);
}
function closeMoreSheet() { const el = document.getElementById('more-sheet'); if (el) el.remove(); }
function toggleMoreSheet() {
  const existing = document.getElementById('more-sheet');
  if (existing) { existing.remove(); return; }
  const groups = effectiveNav().filter((n) => n.group);
  const extras = effectiveNav().filter((n) => !n.group && !BOTTOM_TABS.some((t) => t.id === n.id));
  const item = (it) => h('button', {
    class: 'flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl transition-colors ' + (state.active === it.id ? 'bg-gold-400/10 text-gold-300' : 'text-ink-100 active:bg-base-700'),
    onclick: () => { closeMoreSheet(); navigate(it.id); },
  }, [ h('span', { class: 'text-xl' }, it.emoji), h('span', { class: 'text-sm font-medium' }, it.label) ]);
  const sheet = h('div', {
    id: 'more-sheet',
    class: 'md:hidden fixed inset-0 z-50',
    onclick: (e) => { if (e.target.id === 'more-sheet') closeMoreSheet(); },
  }, [
    h('div', { class: 'absolute inset-0 bg-black/60 backdrop-blur-sm' , onclick: closeMoreSheet }),
    h('div', {
      class: 'absolute bottom-0 left-0 right-0 bg-base-800 border-t border-base-700 rounded-t-3xl max-h-[75vh] overflow-y-auto p-4 animate-fade-in',
      style: 'padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 84px);',
    }, [
      h('div', { class: 'w-10 h-1 rounded-full bg-base-600 mx-auto mb-3' }),
      ...extras.map((e2) => item(e2)),
      ...groups.map((g) => h('div', { class: 'mb-1' }, [
        h('div', { class: 'text-[11px] font-bold uppercase tracking-wider text-ink-400 px-4 pt-3 pb-1' }, g.emoji + ' ' + g.label),
        h('div', { class: 'grid grid-cols-2 gap-1' }, g.children.map(item)),
      ])),
    ]),
  ]);
  document.body.appendChild(sheet);
}

function navigate(sectionId) {
  if (window.__BODEGA_READONLY__ && sectionId !== 'bodega') sectionId = 'bodega';
  state.active = sectionId;
  // Sincronizar URL hash sin disparar evento (para que sectionCard, quickAction etc. funcionen)
  try {
    if (location.hash !== '#' + sectionId) {
      history.replaceState(null, '', '#' + sectionId);
    }
  } catch { /* ignore */ }
  // Actualizar nav visual
  document.querySelectorAll('[data-section]').forEach((btn) => {
    const isActive = btn.dataset.section === sectionId;
    btn.className = (isActive
      ? 'bg-gold-400/10 text-gold-300 border border-gold-400/30'
      : 'text-ink-200 hover:text-ink-100 border border-transparent') + ' px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors';
  });
  document.querySelectorAll('[data-bottom-tab]').forEach((btn) => {
    const isActive = btn.dataset.bottomTab === sectionId;
    btn.className = 'flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 rounded-xl transition-colors ' + (isActive ? 'text-gold-300' : 'text-ink-300');
  });
  renderSection(sectionId);
}

// Escuchar cambios de hash (cuando inicio.js u otros hacen location.hash = '#xxx')
if (!window._carlosHashListener) {
  window._carlosHashListener = true;
  window.addEventListener('hashchange', () => {
    const target = (location.hash || '').replace(/^#/, '').trim();
    if (target && target !== state.active) navigate(target);
  });
}

async function renderSection(id) {
  const mountEl = $('#section-mount');
  if (!mountEl) return;
  mountEl.innerHTML = '<p class="text-ink-300 text-sm">Cargando…</p>';
  const ctx = {
    user: state.user,
    cfg: CUSTOM_CFG,
    builtin: NAV,
    navigate,
    saveCfg: async (newCfg) => { CUSTOM_CFG = newCfg; await api('PUT', '/api/v1/ui-config', { config: newCfg }); },
    reloadUI: async () => { try { const r = await api('GET', '/api/v1/ui-config'); CUSTOM_CFG = (r && r.config) ? r.config : CUSTOM_CFG; } catch (e) {} await renderApp(); },
  };
  try {
    const isCustom = (CUSTOM_CFG.customSections || []).some((s) => s.id === id);
    if (id === 'constructor' || isCustom) {
      const mod = await import('./sections/constructor.js');
      if (id === 'constructor') mod.renderConstructor(mountEl, ctx);
      else mod.renderCustomSection(mountEl, ctx, (CUSTOM_CFG.customSections || []).find((s) => s.id === id));
      return;
    }
    const mod = await import('./sections/index.js');
    mod.renderSection(id, mountEl, ctx);
  } catch (e) {
    console.error('Error sección', id, e);
    mountEl.innerHTML = '<p class="text-red-300">Error cargando sección: ' + (e.message || '') + '</p>';
  }
}

let healthPoll = null;
let syncPoll = null;
function pollHealth() {
  const dot = document.getElementById('api-dot');
  if (!dot) return;
  const update = async () => {
    try {
      const r = await api('GET', '/health');
      dot.className = 'w-2 h-2 rounded-full ' + (r.status === 'ok' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400');
    } catch {
      dot.className = 'w-2 h-2 rounded-full bg-red-400';
    }
  };
  update();
  if (healthPoll) clearInterval(healthPoll);
  healthPoll = setInterval(update, 30000);

  // Sync status poll cada 30s + dispatch CustomEvent 'carlos:sync' que las secciones escuchan para auto-refresh
  const syncTick = async () => {
    if (document.hidden) return;
    try {
      const r = await api('GET', '/api/v1/system/sync-status');
      window.__lastSync = r;
      window.dispatchEvent(new CustomEvent('carlos:sync', { detail: r }));
    } catch {}
  };
  syncTick();
  if (syncPoll) clearInterval(syncPoll);
  syncPoll = setInterval(syncTick, 30000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) syncTick(); });
}

// ============================================================
// BOOTSTRAP
// ============================================================
async function bootstrap() {
  // 1. Splash fuera YA
  const splash = document.getElementById('splash');
  if (splash) {
    splash.style.opacity = '0';
    setTimeout(() => splash.remove(), 300);
  }

  // 2. Decidir signup vs login
  try {
    const status = await api('GET', '/api/v1/auth/status');
    state.needsSignup = status.needsSignup;
  } catch (e) {
    console.warn('No se pudo leer auth/status', e?.message);
  }

  // 3. Intentar /me
  try {
    const me = await api('GET', '/api/v1/auth/me');
    state.user = me;
    state.needsOnboarding = !me.onboardingCompletedAt;
    await renderApp();
    return;
  } catch (e) {
    // sin sesión
  }

  // 4. Sin sesión → login
  renderLogin();
}

// Arrancar inmediatamente
bootstrap().catch((e) => {
  console.error('Bootstrap fatal', e);
  const splash = document.getElementById('splash');
  if (splash) splash.remove();
  const root = $('#app');
  if (root) {
    root.innerHTML = '<div class="flex-1 flex items-center justify-center p-6"><div class="text-center"><h2 class="text-2xl font-bold mb-2">Error al iniciar</h2><p class="text-ink-300 text-sm">' + (e.message || '') + '</p><button onclick="location.reload()" class="mt-4 bg-gold-400 text-base-900 font-semibold px-4 py-2 rounded">Reintentar</button></div></div>';
  }
});

export {};  // marca como módulo
